import { useEffect, useRef } from "react";
import Navbar from "../components/layout/Navbar3";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export default function CancellationPolicyPage() {
  const heroRef = useRef(null);
  const titleRef = useRef(null);
  const contentRef = useRef(null);
  const imageRef = useRef(null);
  const blob1Ref = useRef(null);
  const blob2Ref = useRef(null);
  const blob3Ref = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Hero entrance
      gsap.fromTo(
        titleRef.current,
        { y: 50, opacity: 0 },
        { y: 0, opacity: 1, duration: 1.2, ease: "power4.out", delay: 0.2 }
      );

      // Content sections staggered reveal
      gsap.fromTo(
        ".policy-section",
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: contentRef.current,
            start: "top 80%",
          },
        }
      );

      // Image reveal
      gsap.fromTo(
        imageRef.current,
        { x: 60, opacity: 0, scale: 0.95 },
        {
          x: 0,
          opacity: 1,
          scale: 1,
          duration: 1.2,
          ease: "power3.out",
          delay: 0.4,
        }
      );
    });

    // Mouse parallax for background blobs
    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 2;
      const y = (e.clientY / window.innerHeight - 0.5) * 2;
      if (blob1Ref.current) blob1Ref.current.style.transform = `translate3d(${x * 30}px, ${y * 30}px, 0)`;
      if (blob2Ref.current) blob2Ref.current.style.transform = `translate3d(${x * -40}px, ${y * 20}px, 0)`;
      if (blob3Ref.current) blob3Ref.current.style.transform = `translate3d(${x * 20}px, ${y * -30}px, 0)`;
    };

    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    return () => {
      ctx.revert();
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  const policies = [
    {
      num: "01",
      title: "Free Trials",
      content: "If you are using a free trial, you will not be charged until the trial period ends. You may cancel your subscription at any time during the trial period to avoid charges.",
    },
    {
      num: "02",
      title: "Monthly Subscriptions",
      content: "We do not offer refunds for monthly subscription payments once they are processed. To avoid future charges, you must cancel your subscription before the next billing cycle.",
    },
    {
      num: "03",
      title: "Annual Subscriptions",
      content: "If you cancel an annual subscription within the first 14 days of purchase, you may request a full refund. After this initial period, no refunds will be issued for annual plans, but you will continue to have access to the service for the remainder of the paid term.",
    },
    {
      num: "04",
      title: "Billing Disputes",
      content: (
        <>
          If you believe there has been an error in billing, please contact us at{" "}
          <a href="mailto:info@mnrtechnologies.com" className="text-blue-400 hover:text-blue-300 transition-colors border-b border-blue-400/30 hover:border-blue-400">
            info@mnrtechnologies.com
          </a>{" "}
          within 7 days of the billing date. We will review the matter and respond within a reasonable timeframe.
        </>
      ),
    },
    {
      num: "05",
      title: "Non-Refundable Situations",
      content: (
        <>
          <p className="mb-4">Refunds will not be provided under the following circumstances:</p>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <span className="w-5 h-5 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
              </span>
              <span className="text-gray-400">You failed to cancel before your renewal date.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-5 h-5 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
              </span>
              <span className="text-gray-400">You did not use the service during the subscription period.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-5 h-5 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
              </span>
              <span className="text-gray-400">You are dissatisfied due to reasons unrelated to platform performance.</span>
            </li>
          </ul>
        </>
      ),
    },
    {
      num: "06",
      title: "How to Request a Refund",
      content: (
        <>
          To request a refund (if eligible), please contact us at:{" "}
          <a href="mailto:info@mnrtechnologies.com" className="text-blue-400 hover:text-blue-300 transition-colors border-b border-blue-400/30 hover:border-blue-400">
            info@mnrtechnologies.com
          </a>
          . Please include your full name, account email, and the reason for your request.
        </>
      ),
    },
  ];

  return (
    <div className="bg-slate-950 text-white min-h-screen relative overflow-hidden selection:bg-blue-500/30">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=Playfair+Display:ital,wght@0,400;0,600;1,400;1,600&display=swap');
        .font-playfair { font-family: 'Playfair Display', serif; }
        .font-inter { font-family: 'Inter', sans-serif; }
        .text-gradient-mask {
          background: linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.65) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .noise-overlay {
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.02'/%3E%3C/svg%3E");
        }
      `}</style>

      {/* Noise texture overlay */}
      <div className="fixed inset-0 noise-overlay pointer-events-none z-[1] opacity-30" />

      {/* Background Glow Blobs with mouse parallax */}
      <div ref={blob1Ref} className="fixed -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-blue-600/15 blur-[130px] opacity-60 pointer-events-none z-0 will-change-transform" />
      <div ref={blob2Ref} className="fixed bottom-[-100px] right-[-100px] w-[500px] h-[500px] rounded-full bg-purple-600/15 blur-[110px] opacity-50 pointer-events-none z-0 will-change-transform" />
      <div ref={blob3Ref} className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] rounded-full bg-indigo-600/10 blur-[150px] opacity-40 pointer-events-none z-0 will-change-transform" />

      <Navbar />

      <div className="relative z-10">
        {/* Hero */}
        <section ref={heroRef} className="pt-28 pb-16 px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-4xl mx-auto">
            <p className="text-xs font-medium text-gray-600 uppercase tracking-[0.3em] mb-6">
              Legal
            </p>
            <h1
              ref={titleRef}
              className="font-inter text-4xl sm:text-5xl md:text-7xl font-black tracking-tight text-gradient-mask opacity-0"
            >
              Cancellation &<br />
              <span className="font-playfair italic font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500">
                Refund Policy
              </span>
            </h1>
            <p className="mt-6 text-lg text-gray-500 max-w-2xl mx-auto font-light leading-relaxed">
              Transparency is the foundation of trust. Here's exactly how we handle cancellations, refunds, and billing.
            </p>
          </div>
        </section>

        {/* Section Divider */}
        <div className="relative py-6 flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full h-px bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" />
          </div>
          <div className="relative z-10 w-2 h-2 rounded-full bg-blue-500/40 shadow-[0_0_12px_rgba(59,130,246,0.4)] animate-pulse" />
        </div>

        {/* Main Content */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 items-start">
            {/* Policy Content */}
            <div ref={contentRef} className="flex-1 order-2 lg:order-1">
              <div className="mb-10">
                <p className="text-gray-400 leading-relaxed text-lg font-light">
                  Thank you for subscribing to MNR Technologies. We value your business and strive to provide a reliable and valuable service. This policy outlines the conditions under which refunds are provided for our Software as a Service (SaaS) offerings.
                </p>
              </div>

              <div className="space-y-10">
                {policies.map((policy, index) => (
                  <div
                    key={index}
                    className="policy-section group relative pl-8 border-l border-white/[0.06] hover:border-white/[0.12] transition-colors duration-500"
                  >
                    <span className="absolute left-0 top-0 -translate-x-1/2 font-inter text-[10px] font-bold text-gray-700 bg-slate-950 px-1.5 py-0.5 rounded">
                      {policy.num}
                    </span>
                    <h2 className="font-inter text-xl font-semibold text-white mb-3 tracking-tight">
                      {policy.title}
                    </h2>
                    <div className="text-gray-400 leading-relaxed font-light">
                      {policy.content}
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer note */}
              <div className="mt-16 pt-8 border-t border-white/[0.06]">
                <p className="text-sm text-gray-600">
                  Last updated: May 2024. This policy is subject to change. For questions, contact{" "}
                  <a href="mailto:info@mnrtechnologies.com" className="text-blue-400 hover:text-blue-300 transition-colors border-b border-blue-400/30 hover:border-blue-400">
                    info@mnrtechnologies.com
                  </a>
                </p>
              </div>
            </div>

            {/* Sidebar Image */}
            <div ref={imageRef} className="flex-1 order-1 lg:order-2 lg:sticky lg:top-28 opacity-0">
              <div className="relative group">
                <div className="absolute -inset-2 bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-2xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity duration-500" />
                <img
                  src="/Policy.jpg"
                  alt="Legal documents"
                  width="500"
                  height="700"
                  loading="eager"
                  decoding="async"
                  className="relative w-full h-auto object-cover rounded-2xl border border-white/10 shadow-2xl shadow-black/40"
                />
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}